import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Trophy, Award, Target, Calendar, ClipboardCheck, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const TechnicianScoringPage = () => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    fetchScores();
  }, []);

  const fetchScores = async () => {
    try {
      const params = {};
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const response = await api.get('/technician-scores', { params });
      setScores(response.data);
    } catch (error) {
      console.error('Failed to fetch scores:', error);
      toast.error('Failed to load technician scores');
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    if (startDate && endDate) {
      setLoading(true);
      fetchScores();
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 0) return { icon: Trophy, color: 'text-yellow-500', bg: 'bg-yellow-100', label: '1st' };
    if (rank === 1) return { icon: Award, color: 'text-gray-400', bg: 'bg-gray-100', label: '2nd' };
    if (rank === 2) return { icon: Award, color: 'text-orange-400', bg: 'bg-orange-100', label: '3rd' };
    return { icon: Target, color: 'text-blue-500', bg: 'bg-blue-100', label: `${rank + 1}th` };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="technician-scoring-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Technician Performance</h1>
        <p className="text-lg text-muted-foreground">Point-based performance scoring</p>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-xl border border-border shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row items-end gap-4">
          <div className="flex-1">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-12"
              data-testid="tech-start-date"
            />
          </div>
          <div className="flex-1">
            <Label>End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-12"
              data-testid="tech-end-date"
            />
          </div>
          <Button onClick={handleDateFilter} className="h-12 px-6">
            <Calendar className="w-5 h-5 mr-2" />
            Apply Filter
          </Button>
        </div>
      </div>

      {/* Scoring Legend */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6 mb-6 border border-primary/20">
        <h3 className="font-bold text-lg mb-3">Scoring System</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="font-bold text-2xl text-blue-600">70 pts</p>
              <p className="text-sm text-muted-foreground">per New QC Entry</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="font-bold text-2xl text-green-600">30 pts</p>
              <p className="text-sm text-muted-foreground">per Second Check Completed</p>
            </div>
          </div>
        </div>
      </div>

      {scores.length === 0 ? (
        <div className="bg-white rounded-xl border border-border shadow-sm p-12 text-center">
          <Target className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <p className="text-xl text-muted-foreground">No performance data available yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {scores.map((tech, idx) => {
            const rankBadge = getRankBadge(idx);
            const RankIcon = rankBadge.icon;

            return (
              <div
                key={tech.username}
                data-testid={`tech-score-${tech.username}`}
                className="bg-white rounded-xl border border-border shadow-sm p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  {/* Rank Badge */}
                  <div className="flex items-center justify-center">
                    <div className={`w-20 h-20 rounded-full ${rankBadge.bg} flex flex-col items-center justify-center`}>
                      <RankIcon className={`w-8 h-8 ${rankBadge.color}`} />
                      <span className={`text-sm font-bold ${rankBadge.color}`}>{rankBadge.label}</span>
                    </div>
                  </div>

                  {/* Technician Info */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">{tech.technician_name}</h3>
                    <p className="text-sm text-muted-foreground">@{tech.username}</p>
                  </div>

                  {/* Total Points */}
                  <div className="text-center bg-primary/10 rounded-xl p-4 min-w-[140px]">
                    <p className="text-4xl font-bold text-primary mb-1">{tech.total_points}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Points</p>
                  </div>
                </div>

                {/* Points Breakdown */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{tech.new_qc_count}</p>
                    <p className="text-xs text-muted-foreground">New QCs</p>
                    <p className="text-sm font-medium text-blue-600">+{tech.qc_points} pts</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{tech.second_checks_done}</p>
                    <p className="text-xs text-muted-foreground">Second Checks</p>
                    <p className="text-sm font-medium text-green-600">+{tech.second_check_points} pts</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{tech.pass_count}</p>
                    <p className="text-xs text-muted-foreground">Passed</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{tech.hold_count}</p>
                    <p className="text-xs text-muted-foreground">On Hold</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TechnicianScoringPage;
